interface ReceiptItem {
  name: string;
  amount: number;
  category: "food" | "household" | "health" | "personal" | "other";
  quantity: number;
  price: number;
}

interface Receipt {
  id: string; // receipts.id is a UUID
  date: string;
  store: string;
  total: number;
  items: ReceiptItem[];
  imageUrl: string;
  address?: string;
  currency?: string;
  subtotal?: number;
  store_id?: string | null;
}

/**
 * Exports receipt data as a JSON file
 */
export const exportReceiptAsJSON = (receipt: Receipt) => {
  const exportData = {
    receipt: {
      id: receipt.id,
      store: receipt.store,
      address: receipt.address || "",
      date: receipt.date,
      currency: receipt.currency || "USD",
      subtotal: receipt.subtotal,
      total: receipt.total,
    },
    items: receipt.items.map((item) => ({
      name: item.name,
      category: item.category,
      quantity: item.quantity,
      unitPrice: item.price,
      totalAmount: item.amount,
    })),
  };

  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `receipt-${receipt.store.replace(/[^a-z0-9]/gi, "-")}-${receipt.date}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Exports receipt data as a CSV file
 */
export const exportReceiptAsCSV = (receipt: Receipt) => {
  const currency = receipt.currency || "USD";

  // CSV Header
  let csv = "Receipt Export\n\n";

  // Receipt Information
  csv += "Receipt Information\n";
  csv += `Store,${escapeCSV(receipt.store)}\n`;
  csv += `Address,${escapeCSV(receipt.address || "")}\n`;
  csv += `Date,${receipt.date}\n`;
  csv += `Currency,${currency}\n`;
  if (receipt.subtotal !== undefined) {
    csv += `Subtotal,${receipt.subtotal}\n`;
  }
  csv += `Total,${receipt.total}\n`;
  csv += "\n";

  // Line Items Header
  csv += "Line Items\n";
  csv += "Item Name,Category,Quantity,Unit Price,Total Amount\n";

  // Line Items Data
  receipt.items.forEach((item) => {
    csv += `${escapeCSV(item.name)},${item.category},${item.quantity},${item.price},${item.amount}\n`;
  });

  // Create and download file
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `receipt-${receipt.store.replace(/[^a-z0-9]/gi, "-")}-${receipt.date}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Escapes special characters in CSV fields
 */
const escapeCSV = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};
